/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component (checkout line-item: partecipanti/contact selection,
 *              level assignment, insurance eligibility)
 *   extends:   Component (@glimmer/component)
 *   template:  checkout-product.hbs
 *   args:      @item, @isEditable, @contact, @applicationController
 *   uses:      services store, session, intl; beebee-base/lib/utility cfFilterId;
 *              ember-concurrency restartableTask
 *   siblings:  checkout-detail
 *   tests:     checkout-product-behaviour-test.js, checkout-product-init-test.js,
 *              checkout-product-test.js
 *   gotchas:
 *     - addNewContactTask: `this.args.contact` is absent on the guest-checkout
 *       path — only `contact.get('id')` when contact actually exists, else the
 *       participant record is still created/assigned without a linked contact
 *       (CodeRabbit-flagged crash, fixed).
 *     - changeContactTask: `this.args.contact` is an external record the parent
 *       can unload/swap while this component is mounted; a torn-down record's
 *       async hasMany getter can throw on yield (Sentry
 *       EMBER-SCUOLE-SEGRETERIA-ZC class) — guarded via isDestroyed/isDestroying.
 *     - initTask: an unyielded restartableTask perform is cancelled by the next
 *       loop iteration's perform, so addNewContactTask calls are always yielded.
 *
 * Manages the per-product participant list at checkout: assigns/reassigns
 * contacts, filters selectable levels by discipline (ski/snow/fondo), and
 * computes insurance availability/inclusion for the tenant's price rules.
 */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { set } from '@ember/object';

import { cfFilterId } from 'beebee-base/lib/utility';

import { restartableTask } from 'ember-concurrency';

import { service } from '@ember/service';

export default class CheckoutProductComponent extends Component {
  @service store;
  @service session;
  @service intl;

  @tracked updateContactToSelect = false;
  @tracked contactToSelect = null;
  @tracked componentID = null;
  @tracked showLevelDescription = null;

  @action
  setup(/*element*/) {
    this.initTask.perform();
  }

  @restartableTask
  *initTask() {
    if (this.args.isEditable) {
      if (this.args.item.partecipanti) {
        for (const p of this.args.item.partecipanti) {
          const contactToSelect = yield this.changeContactTask.perform(p);
          if (!Array.isArray(contactToSelect) || contactToSelect.length < 1) {
            yield this.addNewContactTask.perform(p);
          }
        }
      } else {
        const max = Math.max(...this.args.item.ore.map((o) => o.n_pax)) || 0;

        set(this.args.item, 'partecipanti', []);

        for (let i = 0; i < max; i++) {
          const p = {};
          const contactToSelect = yield this.changeContactTask.perform(p);
          if (!Array.isArray(contactToSelect) || contactToSelect.length < 1) {
            // Restartable task: unyielded perform gets cancelled by the next
            // loop iteration's perform. Yield it like the sibling call above.
            yield this.addNewContactTask.perform(p);
          }
          set(this.args.item, 'partecipanti', [...this.args.item.partecipanti, p]);
        }
      }

      const partecipanti = this.args.item.partecipanti;
      partecipanti.forEach((p) => {
        const cloneLevel = [...this.filteredLevel];

        if (this.args.item.item.product_status_id === 92) {
          p.levelToSelect = cloneLevel;
        } else {
          p.levelToSelect = cloneLevel.filter((l) => {
            return (
              !p.level?.colorName ||
              l.colorName === p.level.colorName ||
              l.colorName.includes(p.level.colorName)
            );
          });
        }
      });
    }
  }

  get contactStatus() {
    const componentID = this.componentID;
    const contactToSelect = this.contactToSelect || [];

    return contactToSelect.map((contact) => {
      return {
        contact: contact,
        notSelected: contact[componentID] && contact[componentID].notSelected,
      };
    });
  }

  get showPartecipanti() {
    return !(
      this.args.applicationController.ecommerceLoginBefore &&
      this.args.applicationController.cart.private.length === 0
    );
  }

  get isLevelEditable() {
    return this.args.item?.item?.product_status_id === 92;
  }

  get ecommerce_partecipanti_field() {
    return this.session.option_field?.ecommerce_partecipanti_field;
  }

  get isCFRequired() {
    return this.ecommerce_partecipanti_field?.cod_fiscale;
  }

  get isMailRequired() {
    return this.ecommerce_partecipanti_field?.mail;
  }

  get livelli_sci() {
    if (!this.session.option_field) {
      return [];
    }
    return this.session.option_field?.livelli_sci;
  }

  get levelLangField() {
    if (this.intl.primaryLocale === 'en-us') {
      return {
        ski: 'description_en',
        snow: 'description_snow_en',
      };
    } else if (this.intl.primaryLocale === 'fr-fr') {
      return {
        ski: 'description_fr',
        snow: 'description_snow_fr',
      };
    } else if (this.intl.primaryLocale === 'de-de') {
      return {
        ski: 'description_de',
        snow: 'description_snow_de',
      };
    }

    return {
      ski: 'description',
      snow: 'description_snow',
    };
  }

  get filteredLevel() {
    const livelli = this.livelli_sci;
    const item = this.args.item.item;
    const isSnow = item.is_snow;
    const isFondo = item.is_fondo;
    const isSci = item.is_sci || (!item.is_fondo && !item.is_snow);

    return livelli.filter(function (l) {
      if (!l.hide_ecommerce) {
        if (isSnow) {
          return l.isSnowboard;
        } else if (isFondo) {
          return l.isFondo;
        } else if (isSci) {
          return !l.hideSci;
        }
        return true;
      }
      return false;
    });
  }

  get assuranceDescription() {
    var option_field = this.session.option_field?.assicurazione;

    if (option_field) {
      return {
        ita: option_field[this.args.item.item.product_status_id]?.description_ita || false,
        fra: option_field[this.args.item.item.product_status_id]?.description_fra || false,
        eng: option_field[this.args.item.item.product_status_id]?.description_en || false,
        deu: option_field[this.args.item.item.product_status_id]?.description_de || false,
      };
    }

    return {
      ita: '',
      fra: '',
      eng: '',
    };
  }

  get canAssurance() {
    if (!this.args.item.item.product_status_id) {
      return false;
    }

    var option_field = this.session.option_field?.assicurazione;

    if (option_field) {
      var isFix = option_field[this.args.item.item.product_status_id]
        ? option_field[this.args.item.item.product_status_id].fix
        : false;
      var isPerc = option_field[this.args.item.item.product_status_id]
        ? option_field[this.args.item.item.product_status_id].perc
        : false;

      return isFix || isPerc;
    }

    return false;
  }

  get assIncluded() {
    if (!this.session.option_field || !this.args.item.item.product_status_id) {
      return false;
    }

    var option_field = this.session.option_field?.assicurazione;

    if (option_field) {
      return option_field[this.args.item.item.product_status_id]
        ? option_field[this.args.item.item.product_status_id].included
        : false;
    }

    return false;
  }

  @restartableTask
  *addNewContactTask(partecipante) {
    if (!partecipante) {
      return false;
    }

    let contact = this.args.contact;
    const par = this.store.createRecord('contact', {
      contact_state: this.store.peekRecord('contact_state', 114),
    });

    if (this.args.item.item.is_snow) {
      if (partecipante.snow_color?.colorName) {
        par.set('snow_color', partecipante.snow_color.colorName);
      }
    } else if (this.args.item.item.is_fondo) {
      if (partecipante.fondo_color?.colorName) {
        par.set('fondo_color', partecipante.fondo_color.colorName);
      }
    } else {
      if (partecipante.level?.colorName) {
        par.set('color', partecipante.level.colorName);
      }
    }

    if (partecipante.level?.colorName) {
      if (this.args.item.item.is_snow) {
        par.set('snow_color', partecipante.level.colorName);
      } else if (this.args.item.item.is_fondo) {
        par.set('fondo_color', partecipante.level.colorName);
      } else {
        par.set('color', partecipante.level.colorName);
      }
    }

    // `this.args.contact` is absent in the guest-checkout path: changeContactTask
    // returns an empty array for a null/destroyed contact, and the caller then
    // runs THIS task — so `contact.get('id')` here derefs null and crashed
    // (CodeRabbit critical). Still create/assign the participant record; only
    // link a related contact when one actually exists.
    if (contact && contact.get('id')) {
      contact = this.store.peekRecord('contact', contact.get('id'));
    }

    const related_contacts = yield par.related_contacts;
    if (contact) {
      related_contacts.push(contact);
    }

    set(partecipante, 'contact', par);
    set(partecipante, 'editContact', true);
  }

  @restartableTask
  *changeContactTask(partecipante) {
    if (!partecipante) {
      return false;
    }

    set(partecipante, 'contact', null);
    set(partecipante, 'editContact', null);

    let contactToSelect = [];

    // this.args.contact is an external record the parent can unload/swap while
    // this component is mounted; a torn-down record's async hasMany getter
    // returns a PromiseManyArray whose internal promise is null, so `yield`ing
    // it throws "can't access property 'then', this.promise is null"
    // (Sentry EMBER-SCUOLE-SEGRETERIA-ZC class).
    const contact = this.args.contact;
    if (!contact || contact.isDestroyed || contact.isDestroying) {
      // Reset the tracked prop too: the happy path assigns it at the end, so
      // without this the early return leaves a stale contactToSelect from a
      // previous participant, which the `me` getter would render.
      this.contactToSelect = contactToSelect;
      return contactToSelect;
    }

    const related_contacts = yield contact.related_contacts;
    if (related_contacts.length > 0) {
      contactToSelect = related_contacts.filter((item) => item.get('contact_state.id') === '114');
    }

    const componentID = this.componentID || Math.random().toString(36).substr(2, 9);
    this.componentID = componentID;

    if (!this.isLevelEditable) {
      if (this.args.item.item.is_snow) {
        for (const c of contactToSelect) {
          // custom_fields e' una hasMany async: c.get('custom_fields') e' una
          // PromiseManyArray, e cfFilterId richiede Array.isArray -> tornava
          // sempre null (livello mai trovato). Risolvo e passo un array nativo.
          const cfs = yield c.custom_fields;
          const snow_color = cfFilterId(Array.from(cfs ?? []), '259');

          if (snow_color) {
            set(c, 'snow_color', snow_color.get('value'));
          }
        }

        if (partecipante.snow_color) {
          contactToSelect = contactToSelect.filter(
            (item) => item.snow_color === partecipante.snow_color.colorName
          );
        }
      } else if (this.args.item.item.is_fondo) {
        for (const c of contactToSelect) {
          const cfs = yield c.custom_fields;
          const fondo_color = cfFilterId(Array.from(cfs ?? []), '610');
          if (fondo_color) {
            set(c, 'fondo_color', fondo_color.get('value'));
          }
        }

        if (partecipante.fondo_color) {
          contactToSelect = contactToSelect.filter(
            (item) => item.fondo_color === partecipante.fondo_color.colorName
          );
        }
      } else {
        if (partecipante.level) {
          contactToSelect.forEach((item) => {
            item[componentID] = item[componentID] || {};
            item[componentID].test = 'Ciao';
            set(item, `${componentID}.prova`, 'Ciao1');
            item[componentID].notSelected = item.color !== partecipante.level.colorName;
          });
        }
      }
    }

    this.contactToSelect = contactToSelect;
    return contactToSelect;
  }

  @restartableTask
  *updateContactTask(contact) {
    if (contact.get('loaded')) {
      return;
    }

    const c = yield this.store.findRecord('contact', contact.get('id'), {
      reload: true,
    });

    const cfs = yield c.custom_fields;
    let snow_color = cfFilterId(Array.from(cfs ?? []), '259');
    if (snow_color) {
      snow_color = snow_color.value;
    }

    let fondo_color = cfFilterId(Array.from(cfs ?? []), '610');
    if (fondo_color) {
      fondo_color = fondo_color.value;
    }

    if (Array.isArray(this.livelli_sci)) {
      this.livelli_sci.forEach(function (l) {
        if (l.colorName === snow_color) {
          set(c, 'snow_color', l.colorName);
        } else if (l.colorName === fondo_color) {
          set(c, 'fondo_color', l.colorName);
        }
      });
    }

    set(c, 'loaded', true);
  }

  @action
  removeColor(item, dispo = null) {
    const value = this.args.item.item.is_snow
      ? 'contact.snow_color'
      : this.args.item.item.is_fondo
        ? 'contact.fondo_color'
        : 'contact.color';

    if (item && value) {
      set(item, value, null);
      set(item, 'contact.tmpLevel', null);
    }
  }

  @action
  setColor(item, dispo = null, level = null) {
    const value = this.args.item.item.is_snow
      ? 'snow_color'
      : this.args.item.item.is_fondo
        ? 'fondo_color'
        : 'color';

    if (item && value && level) {
      set(item, value, level.colorName);
    }
  }

  @action
  selectPartecipante(partecipante, contact) {
    if (!contact.get('id') || contact.notSelected) {
      return false;
    }

    set(partecipante, 'contact', contact);
    this.updateContactTask.perform(contact);
    this.updateContactToSelect = !this.updateContactToSelect;
  }

  @action
  newContact(partecipante) {
    this.addNewContactTask.perform(partecipante);
  }
}
